import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import { getContractSourceDocument } from '../api/abfApi';
import EvidencePanel from '../onboarding/review/EvidencePanel';
import { INK } from '../onboarding/review/reviewStyles';
import { Readable } from '../onboarding/review/contractReviewStyles';
import type { ReviewRow } from '../onboarding/review/reviewModel';
import type { ReadinessKind, ReadinessSectionKey } from '../types/activationReadiness';
import type { StatusTone } from '../components/StatusChip';
import type { FacilityContractVersion } from '../types/contractWorkspace';

/**
 * F-MK-084: every source on the Contract tab opens the review page's own source panel at the
 * document and page the row was read from — after activation as before it.
 *
 * An amendment carries most of its rows from the version it amends, so the version's own PDF is
 * the wrong document for them. Terms carry their document job; rules name it as their source
 * reference; covenants carry it until they are carried forward. Every schedule row records the row
 * it was carried from, so a carried row is followed back to where it was first read. A row with no
 * document behind it says it was keyed, and by whom.
 */

export type SourceTable = 'terms' | 'eligibilityRules' | 'covenants' | 'waterfallNodes' | 'consequences'
  | 'lenderClasses' | 'conditionsPrecedent' | 'contractAccounts' | 'paymentCalendar';

/** What the reader clicked: the row as the tab shows it, and where it says it came from. */
export interface SourceRequest {
  uid: string;
  table: SourceTable;
  /** The row itself, when the tab has it (an extracted, not yet materialized account has none). */
  row: Record<string, unknown> | null;
  kind: ReadinessKind;
  section: ReadinessSectionKey;
  label: string;
  group?: string;
  value: string;
  detail?: string | null;
  page?: string | null;
  sectionRef?: string | null;
  clause?: string | null;
  confidence?: number | null;
  required?: boolean;
  material?: boolean;
}

export interface Provenance {
  jobId: string | null;
  /** The version whose document it is (an inherited row: the version that first stated it). */
  version: FacilityContractVersion | null;
  keyedBy: string | null;
  keyedAt: string | null;
}

const text = (value: unknown): string | null => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed ? trimmed : null;
};
const JOB_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The document job a row names itself, for the tables that record one per row. */
const ownJob = (table: SourceTable, row: Record<string, unknown>): string | null => {
  if (table === 'terms' || table === 'covenants') return text(row.source_document_job_id);
  if (table === 'eligibilityRules') {
    const reference = text(row.source_reference);
    return reference && JOB_ID.test(reference) ? reference : null;
  }
  return null;
};
/** Tables whose rows name their own document: a row of these without one was keyed. */
const PER_ROW_DOCUMENT = new Set<SourceTable>(['terms', 'eligibilityRules', 'covenants']);

/** Resolves any row of any loaded version to the document it was read from. */
export const useProvenance = (versions: FacilityContractVersion[]) => useMemo(() => {
  const index = new Map<string, { row: Record<string, unknown>; version: FacilityContractVersion }>();
  const byId = new Map(versions.map(version => [version.id, version]));
  const jobOwner = new Map<string, FacilityContractVersion>();
  // Oldest first, so a document is owned by the version that introduced it.
  [...versions].sort((a, b) => a.version_no - b.version_no).forEach(version => {
    if (version.source_document_job_id && !jobOwner.has(version.source_document_job_id)) jobOwner.set(version.source_document_job_id, version);
    const add = (table: SourceTable, rows: unknown) => (Array.isArray(rows) ? rows : rows ? [rows] : [])
      .forEach(item => { const row = item as Record<string, unknown>; if (row?.id != null) index.set(`${table}:${row.id}`, { row, version }); });
    add('terms', version.terms);
    add('eligibilityRules', version.eligibilityRules);
    add('covenants', version.covenants);
    add('waterfallNodes', version.waterfallNodes);
    add('consequences', version.consequences);
    add('lenderClasses', version.lenderClasses);
    add('conditionsPrecedent', version.conditionsPrecedent);
    add('contractAccounts', version.contractAccounts);
    add('paymentCalendar', version.paymentCalendar);
  });

  const resolve = (request: SourceRequest, owner: FacilityContractVersion): Provenance => {
    const keyed = (row: Record<string, unknown> | null, version: FacilityContractVersion): Provenance => ({
      jobId: null, version,
      keyedBy: text(row?.verified_by) || text(row?.created_by) || version.verified_by || version.reviewed_by || null,
      keyedAt: text(row?.verified_at) || text(row?.created_at) || version.verified_at || null,
    });
    // An extracted account or calendar not yet materialized was read from this version's document.
    if (!request.row) return owner.source_document_job_id
      ? { jobId: owner.source_document_job_id, version: owner, keyedBy: null, keyedAt: null } : keyed(null, owner);
    let row = request.row;
    // A schedule in force may be another version's row; it belongs to that version.
    let version = byId.get(Number(row.facility_version_id)) || owner;
    for (let hop = 0; hop < 16; hop++) {
      const job = ownJob(request.table, row);
      if (job) return { jobId: job, version: jobOwner.get(job) || version, keyedBy: null, keyedAt: null };
      const prior = row.carried_from_id != null ? index.get(`${request.table}:${row.carried_from_id}`) : undefined;
      if (!prior) break;
      row = prior.row;
      version = prior.version;
    }
    if (!PER_ROW_DOCUMENT.has(request.table) && version.source_document_job_id) {
      return { jobId: version.source_document_job_id, version, keyedBy: null, keyedAt: null };
    }
    return keyed(row, version);
  };

  const documentOf = (jobId: string | null) => {
    const version = jobId ? jobOwner.get(jobId) : undefined;
    if (!version) return { name: 'Source document', noun: 'document', short: 'Document' };
    return version.predecessor_version_id != null
      ? { name: `Amendment (version ${version.version_no})`, noun: 'amendment', short: 'Amendment' }
      : { name: `Credit agreement (version ${version.version_no})`, noun: 'agreement', short: 'Agreement' };
  };

  return { resolve, documentOf };
}, [versions]);

// ----------------------------------------------------------------------------- the source link

const LinkButton = styled.button`
  && {
    display: inline; min-height: 0; padding: 0; border: 0; background: none; cursor: pointer;
    font: inherit; font-size: 12px; line-height: 1.45; text-align: left; color: ${INK.accentSoft};
    text-decoration: underline dotted ${INK.faint}; text-underline-offset: 3px;
  }
  &&:hover { color: ${INK.strong}; text-decoration-style: solid; }
  &&[data-keyed='true'] { color: ${INK.muted}; }
  &&[aria-pressed='true'] { color: ${INK.strong}; text-decoration: underline solid ${INK.accent}; }
  && span { font-size: 12px; }
  && .doc { color: ${INK.faint}; margin-right: 5px; text-decoration: none; }
`;

/** The SOURCE cell: the row's locator, a link that opens the source panel. */
export const SourceLink: React.FC<{
  request: SourceRequest;
  provenance: Provenance;
  /** Shown before the locator when the version reads rows from more than one document. */
  documentTag?: string | null;
  extra?: string | null;
  active: boolean;
  onOpen: (request: SourceRequest) => void;
}> = ({ request, provenance, documentTag, extra, active, onOpen }) => {
  const sectionText = request.sectionRef ? (/^\s*(section|§)/i.test(request.sectionRef) ? request.sectionRef : `Section ${request.sectionRef}`) : null;
  const locator = [sectionText, request.page ? `page ${request.page}` : null].filter(Boolean).join(' · ');
  const clause = request.clause ? String(request.clause).replace(/^Credit Agreement, /, '') : '';
  const label = locator || (clause ? clause.slice(0, 48) + (clause.length > 48 ? '…' : '') : '')
    || (provenance.jobId ? 'Open the document' : `Keyed by ${provenance.keyedBy || 'a user'}`);
  return <>
    <LinkButton type="button" aria-pressed={active} data-keyed={!provenance.jobId}
      title={provenance.jobId ? 'Open the source at its page' : 'Keyed by hand; no document behind it'}
      onClick={() => onOpen(request)}>
      {documentTag && provenance.jobId && <span className="doc">{documentTag}</span>}{label}
    </LinkButton>
    {extra && <span style={{ color: INK.faint, fontSize: 12 }}> · {extra}</span>}
  </>;
};

// ----------------------------------------------------------------------------- the side panel

const Drawer = styled.aside`
  position: fixed; top: 101px; right: 0; bottom: 0; z-index: 150; width: min(640px, 100vw);
  display: flex; flex-direction: column; background: #0b1424; color: ${INK.body};
  border-left: 1px solid ${INK.lineStrong}; box-shadow: -8px 0 30px #02061766;
  .bar { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 10px 14px; border-bottom: 1px solid ${INK.line}; }
  .bar span { color: ${INK.muted}; font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .bar button { display: inline-flex; align-items: center; gap: 5px; min-height: 32px; padding: 5px 10px; border: 1px solid ${INK.lineStrong}; border-radius: 7px; background: transparent; color: ${INK.accentSoft}; cursor: pointer; font: inherit; font-size: 12px; }
  .bar button:hover { border-color: ${INK.accent}; }
  .body { flex: 1; min-height: 0; overflow-y: auto; overscroll-behavior: contain; padding: 14px; }
  @media (max-width: 700px) { top: 0; }
`;

const sectionWord: Partial<Record<ReadinessKind, string>> = {
  TERM: 'Term', RULE: 'Eligibility rule', COVENANT: 'Covenant', SCHEDULE: 'Schedule', ACCOUNT: 'Account', CALENDAR: 'Payment calendar', CONDITION: 'Condition precedent',
};

/** The review page's source panel, in a side drawer, for one row of the selected version. */
export const ContractSourceDrawer: React.FC<{
  request: SourceRequest;
  provenance: Provenance;
  selected: FacilityContractVersion;
  document: { name: string; noun: string };
  chip: { tone: StatusTone; label: string };
  onClose: () => void;
}> = ({ request, provenance, selected, document: source, chip, onClose }) => {
  const closeRef = useRef<HTMLButtonElement>(null);
  const urls = useRef(new Map<string, string>());
  useEffect(() => {
    const cache = urls.current;
    return () => { cache.forEach(url => URL.revokeObjectURL(url)); cache.clear(); };
  }, []);
  useEffect(() => { closeRef.current?.focus(); }, [request.uid]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && !(event.target as Element)?.closest?.('dialog')) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const jobId = provenance.jobId;
  // The version asked is the one being read; the server serves the job only if that version or its facility cites it.
  const loadDocument = useCallback(async () => {
    if (!jobId) throw new Error('No document');
    const cached = urls.current.get(jobId);
    if (cached) return cached;
    const blob = await getContractSourceDocument(selected.id, jobId);
    const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
    urls.current.set(jobId, url);
    return url;
  }, [jobId, selected.id]);

  const inherited = provenance.version && provenance.version.id !== selected.id ? provenance.version : null;
  const row: ReviewRow = {
    uid: request.uid, kind: request.kind, section: request.section, id: null, key: request.uid,
    label: request.label, group: request.group || '', value: request.value, detail: request.detail ?? null,
    extractedValue: null, corrected: false,
    citation: request.page || request.sectionRef || request.clause
      ? { page: request.page || null, section: request.sectionRef || null, clause: request.clause || null, excerpt: null } : null,
    entered: jobId ? null : { by: provenance.keyedBy || 'a user', at: provenance.keyedAt, source: '' },
    confidence: request.confidence ?? null,
    status: 'INFO', problem: null, message: null, owner: null, actions: [], valueType: null,
    required: request.required ?? true, material: request.material ?? false,
  };
  const note = jobId
    ? `Read from the ${source.name.charAt(0).toLowerCase()}${source.name.slice(1)}${inherited ? `; carried into version ${selected.version_no} unchanged` : ''}.`
    : inherited ? `Entered on version ${inherited.version_no}; carried into version ${selected.version_no} unchanged.` : null;

  return <Drawer role="complementary" aria-label={`Source for ${request.label}`}>
    <div className="bar">
      <span>{sectionWord[request.kind] || 'Source'} · version {selected.version_no}{jobId ? ` · ${source.name}` : ' · keyed, no document'}</span>
      <button ref={closeRef} type="button" onClick={onClose} aria-label="Close the source panel"><X size={13} /> Close</button>
    </div>
    <div className="body">
      {/* The review page's type scale; one panel per document, so moving between rows of the same
          document keeps its viewer open at the new page. */}
      <Readable>
        <EvidencePanel key={jobId || 'keyed'} row={row} documentName={source.name} canShowDocument={Boolean(jobId)}
          loadDocument={loadDocument} versionId={selected.id} chip={chip} sourceNote={note} documentNoun={source.noun} />
      </Readable>
    </div>
  </Drawer>;
};
